import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingBag } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import { fetchShopProducts } from "@/lib/api";
import { breadcrumbJsonLd } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ProductCard({ p }) {
    return (
        <Link to={`/shop/${p.slug}`} data-testid={`product-card-${p.slug}`}
            className="glass glow-border group block overflow-hidden rounded-lg transition hover:border-gold-300/40">
            <div className="relative aspect-square w-full overflow-hidden bg-white/[0.03]">
                {p.image ? (
                    <img src={`${BACKEND}${p.image}`} alt={p.title} loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center"><Package size={44} className="text-gold-300/40" /></div>
                )}
                {p.sold_out && <span className="absolute left-3 top-3 rounded-full bg-red-500/85 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white">Sold Out</span>}
                {!p.sold_out && p.stock_left != null && <span className="absolute left-3 top-3 rounded-full bg-gold-300/90 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-black">Only {p.stock_left} left</span>}
            </div>
            <div className="p-6">
                {p.category && <p className="text-[11px] uppercase tracking-wider text-white/45">{p.category}</p>}
                <h3 className="mt-2 font-display text-lg leading-snug">{p.title}</h3>
                {p.short_description && <p className="mt-2 text-sm leading-relaxed text-white/55 line-clamp-2">{p.short_description}</p>}
                <div className="mt-4 flex items-center justify-between">
                    <p className="font-display text-lg gold-text">₹{(p.price || 0).toLocaleString("en-IN")}
                        {p.strike_price && <span className="ml-2 text-sm text-white/35 line-through">₹{p.strike_price.toLocaleString("en-IN")}</span>}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gold-300"><ShoppingBag size={13} /> Buy Now</span>
                </div>
            </div>
        </Link>
    );
}

export default function Shop() {
    const [items, setItems] = useState(null);
    useEffect(() => { fetchShopProducts().then(setItems).catch(() => setItems([])); }, []);
    return (
        <div data-testid="shop-page" className="relative">
            <Seo
                title="Shop — Books, Kits & Business Growth Products | Abhijit JK"
                description="Shop curated business growth products by Abhijit JK — books, planners, kits and tools for founders and teams. Direct Buy Now checkout with shipping across India."
                path="/shop"
                jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }])]}
            />
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-10 lg:px-10">
                <Reveal><SectionEyebrow>Shop Now</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
                        Tools you can <span className="gold-text">hold in your hands.</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/65 md:text-lg">
                        Physical products crafted for growth-minded founders and teams. Simple direct
                        checkout — no cart, no friction. Shipped to your door.
                    </p>
                </Reveal>
            </section>
            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                {items === null ? (
                    <p className="text-white/50">Loading products…</p>
                ) : items.length === 0 ? (
                    <div className="glass rounded-lg p-10 text-center text-white/60" data-testid="shop-empty">
                        New products are on the way. Check back soon.
                    </div>
                ) : (
                    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((p) => <Reveal key={p.id}><ProductCard p={p} /></Reveal>)}
                    </div>
                )}
            </section>
        </div>
    );
}
